/** tm/components/govRuleText.ts — 세부 유형(tracks)·확장 조건(conditions) 규칙 → 문장형 칩 글 (2026-10-05)
 *
 *  등록 양식(GovProgramForm 「추가 조건」·「세부 유형」)과 공고 검토함 AI 근거 상자(GovFeedParts EVIDENCE_ROWS)가 같은 글을 쓴다.
 *  저장값은 서버(services/govTracks.js)가 정규화하지만, 화면은 깨진 값도 받을 수 있게 모든 값을 unknown 으로 보고 확인한다.
 *  형식이 틀린 키는 「형식 오류 · 매출 감소율」 칩으로 보여 지울 수 있게 한다(조용히 숨기면 저장할 때 400 원인을 못 찾는다).
 */
import type { GovConditions, GovTrack, GovTrackUnit } from '../types';
import { subLabel, parseSubKey, ANY_PARENT, anyIndKeys } from './GovTaxonomy';

export type RuleObj = Record<string, unknown>;

/** 칩으로 보이는 규칙 키 순서 — 트랙: 자주 쓰는 것 먼저 */
export const TRACK_RULE_ORDER = ['emp', 'biz', 'subs', 'tech', 'scale', 'yrs', 'age', 'startup', 'sales', 'rg', 'rgd', 'nomicro', 'types', 'spec', 'arts', 'cls', 'pat', 'tm',
  'bizx', 'rgx', 'drop', 'exp', 'opened', 'any', 'chk'] as const;
/** 공고 공통 확장 조건 — meta 는 내부 보관(화면에 안 보임) · tech(기술 기반 창업)는 체크 칸으로 보인다(ConditionsRow) */
export const COND_ORDER = ['tech', 'bizx', 'rgx', 'drop', 'exp', 'opened', 'nomicro', 'any', 'chk'] as const;

/** 화면에서 고칠 수 있는 종류 (나머지는 보기·지우기만) */
export type EditKind = 'bizx' | 'rgx' | 'drop' | 'exp' | 'opened' | 'chk' | 'emp' | 'biz' | 'scale' | 'yrs' | 'age';
export const EDIT_KINDS: readonly EditKind[] = ['bizx', 'rgx', 'drop', 'exp', 'opened', 'chk', 'emp', 'biz', 'scale', 'yrs', 'age'];
export const isEditKind = (k: string): k is EditKind => (EDIT_KINDS as readonly string[]).includes(k);

export const KIND_LABEL: Record<string, string> = {
  bizx: '업종 제외', rgx: '지역 제외', drop: '매출 감소율', exp: '수출실적', opened: '개업일', chk: 'FP 확인 항목',
  emp: '상시근로자', biz: '업종', scale: '기업규모', yrs: '업력', age: '대표 나이',
  startup: '창업기업', sales: '연매출', rg: '지역', rgd: '시·군·구', nomicro: '소상공인 제외', micro_ok: '소상공인 예외', tech: '기술 기반 창업', types: '기업 유형', arts: '전용 분야', subs: '세부업종', spec: '특수 자격',
  cls: '분류', pat: '특허', tm: '상표', any: '「또는」 조건',
};

/** 기술 기반 창업 조건 글 (2026-10-05l) — 업종을 직접 정하지 않았으면 제조·연구개발·SW 업종 한정 */
export const TECH_TEXT = '기술 기반 창업 — 제조·연구개발·SW';
export const OP_WORD: Record<string, string> = { '<': '미만', '<=': '이하', '>=': '이상', '>': '초과' };
export const SCALE_WORD: Record<string, string> = { micro: '소상공인만', small: '소기업까지', ms: '중소기업까지', msmid: '중견기업까지' };
export const SCALE_CODES = ['micro', 'small', 'ms', 'msmid'] as const;
export const SCALE_NAME: Record<string, string> = { micro: '소상공인', small: '소기업', ms: '중소기업', msmid: '중견기업' };
export const UNIT_OPTIONS: { v: GovTrackUnit; label: string }[] = [
  { v: '', label: '단위 없음' }, { v: '사업장', label: '사업장당' }, { v: '1인', label: '1인당' }, { v: '현장', label: '현장당' }, { v: '월', label: '월' },
];

export const isObj = (v: unknown): v is RuleObj => v != null && typeof v === 'object' && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
/** 숫자 칸 — 숫자 또는 숫자 문자열 (AI·옛 값) */
export const numOf = (v: unknown): number | null => {
  if (isNum(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v.replace(/,/g, '')))) return Number(v.replace(/,/g, ''));
  return null;
};
const strList = (v: unknown): string[] | null => {
  if (typeof v === 'string') { const l = v.split(',').map(x => x.trim()).filter(Boolean); return l.length ? l : null; } // 서버도 쉼표 문자열을 받는다
  if (!Array.isArray(v)) return null;
  const a = v.filter((x): x is string | number => typeof x === 'string' || typeof x === 'number').map(x => String(x).trim()).filter(Boolean);
  return a.length === v.length ? a : null;
};
/** 3 → '3', 2.5 → '2.5', 12000 → '12,000' */
export const fmtNum = (n: number): string => n.toLocaleString('ko-KR', { maximumFractionDigits: 2 });
/** 'YYYY-MM-DD' → ''25.1.1' */
export const fmtShortDate = (s: unknown): string | null => {
  if (typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `'${m[1].slice(2)}.${Number(m[2])}.${Number(m[3])}` : null;
};
/** 100000 USD → '10만불' · 5000 → '5,000달러' */
export const fmtUsd = (usd: number): string => (usd >= 10000 ? `${fmtNum(usd / 10000)}만불` : `${fmtNum(usd)}달러`);

/** min·max → '3~7년' · '3년 이상' · '7년 이하' (unit 붙임) */
function rangeText(v: unknown, unit: string): string | null {
  if (!isObj(v)) return null;
  const lo = v.min == null ? null : numOf(v.min);
  const hi = v.max == null ? null : numOf(v.max);
  if ((v.min != null && lo == null) || (v.max != null && hi == null) || (lo == null && hi == null)) return null;
  if (lo != null && hi != null) return `${fmtNum(lo)}~${fmtNum(hi)}${unit}`;
  return lo != null ? `${fmtNum(lo)}${unit} 이상` : `${fmtNum(hi as number)}${unit} 이하`;
}

/** 세부업종 키 → 글 — 「세부업종: 제조업 › 식품·화장품 · 무관 › 해양」 (2026-10-05q 「업종 › 세부업종」 · 켜진 칩은 전부 하나라도 — 엔진 bizText 와 같은 꼴)
 *  업종마다 한 묶음(표 순서대로 처음 나온 업종부터) · 무관 › 세부업종은 맨 뒤 · 모르는 키는 그대로 */
export function subsText(l: string[] | null): string | null {
  if (!l || !l.length) return null;
  const order: string[] = [], by: Record<string, string[]> = {}, odd: string[] = [];
  for (const k of l) {
    const x = parseSubKey(k);
    if (!x) { odd.push(subLabel(k)); continue; }
    const p = x.p === ANY_PARENT ? ANY_PARENT : x.p;
    if (!by[p]) { by[p] = []; if (p !== ANY_PARENT) order.push(p); }
    if (!by[p].includes(x.s)) by[p].push(x.s);
  }
  if (by[ANY_PARENT]) order.push(ANY_PARENT);
  const parts = order.map(p => `${p === ANY_PARENT ? '무관' : p} › ${by[p].join('·')}`).concat(odd);
  return parts.length ? `세부업종: ${parts.join(' · ')}` : null;
}

/** 규칙 한 키 → 칩 글 (형식이 틀리면 null). chk 는 항목마다 따로 — 여기선 다루지 않는다 */
export function ruleText(k: string, v: unknown): string | null {
  switch (k) {
    case 'bizx': case 'rgx': { const l = strList(v); return l && l.length ? `${l.join('·')} 제외` : null; }
    case 'biz': { const l = strList(v); return l && l.length ? `${l.join('·')}만` : null; }
    case 'subs': return subsText(strList(v));
    case 'rg': case 'rgd': case 'types': case 'spec': case 'arts': case 'cls': {
      const l = strList(v); return l && l.length ? `${KIND_LABEL[k]}: ${l.join('·')}` : null;
    }
    case 'drop': { const p = isObj(v) ? numOf(v.pct) : null; return p != null ? `매출 ${fmtNum(p)}% 이상 감소` : null; }
    case 'exp': {
      const u = isObj(v) ? numOf(v.usd) : null;
      const op = isObj(v) ? v.op : null;
      return u != null && (op === '<' || op === '>=') ? `수출 ${fmtUsd(u)} ${op === '<' ? '미만' : '이상'}` : null;
    }
    case 'opened': {
      if (!isObj(v)) return null;
      const b = v.before ? fmtShortDate(v.before) : null;
      const a = v.after ? fmtShortDate(v.after) : null;
      if ((v.before && !b) || (v.after && !a) || (!a && !b)) return null;
      if (a && b) return `${a}~${b} 개업`;
      return b ? `${b} 이전 개업` : `${a} 이후 개업`;
    }
    case 'emp': {
      const n = isObj(v) ? numOf(v.n) : null;
      const op = isObj(v) && typeof v.op === 'string' ? OP_WORD[v.op] : undefined;
      return n != null && op ? `상시근로자 ${fmtNum(n)}명 ${op}` : null;
    }
    case 'sales': {
      const n = isObj(v) ? numOf(v.eok) : null;
      const op = isObj(v) && typeof v.op === 'string' ? OP_WORD[v.op] : undefined;
      return n != null && op ? `연매출 ${fmtNum(n)}억 ${op}` : null;
    }
    case 'scale': return typeof v === 'string' && SCALE_WORD[v] ? SCALE_WORD[v] : null;
    case 'yrs': { const t = rangeText(v, '년'); return t ? `업력 ${t}` : null; }
    case 'age': { const t = rangeText(v, '세'); return t ? `대표 ${t}` : null; }
    case 'startup': return v === true ? '창업기업만' : v === 'exclude' ? '비창업만' : null;
    case 'nomicro': return v === true ? '소상공인 제외' : null;
    case 'micro_ok': {   // 소상공인 제외의 예외(공고문 단서 · 2026-10-26)
      if (!isObj(v)) return null;
      if (v.all === true) return '소상공인도 신청 가능';
      const ck = isObj(v.check) ? v.check : null;
      const ex = [...(Array.isArray(v.biz) ? v.biz.map(String) : []), ...(Array.isArray(v.types) ? v.types.map(String) : []), ...(ck && ck.t ? [`${String(ck.t)}(확인)`] : [])];
      return ex.length ? `소상공인 예외 ${ex.join(' · ')}` : null;
    }
    case 'tech': return v === true ? TECH_TEXT : null;
    case 'pat': return v === true ? '특허 보유' : null;
    case 'tm': return v === true ? '상표 보유' : null;
    case 'any': {
      if (!Array.isArray(v) || !v.length) return null;
      const ind = anyIndKeys(v);   // 세부업종 키뿐인 묶음(업종 키 · 무관 키 섞여도) = 세부업종(하나라도) — 「또는」로 보이지 않는다 (2026-10-05n · 05q)
      if (ind) return subsText(ind);
      const parts = v.map(g => (isObj(g) ? rulesSummary(g) : '')).filter(Boolean);
      return parts.length === v.length ? `또는: ${parts.join(' / ')}` : null;
    }
    default: return null;
  }
}

/** 규칙 묶음 한 줄 (또는 묶음 원소 · AI 근거 상자용) — 알 수 없는 키·chk 는 뺀다 */
export function rulesSummary(r: RuleObj): string {
  return TRACK_RULE_ORDER.filter(k => k !== 'chk' && r[k] !== undefined).map(k => ruleText(k, r[k])).filter((t): t is string => !!t).join('·');
}

export interface RuleChip {
  /** 규칙 키 */
  k: string;
  /** chk 항목 순번 */
  idx?: number;
  text: string;
  /** 형식이 틀린 값 — 지우기만 */
  bad?: boolean;
  /** FP 확인 항목 (판정 영향 없음) */
  check?: boolean;
}

/** 규칙 객체 → 칩 목록 (order 순, 그 밖의 키는 뒤에 「형식 오류」로 — meta·id 같은 보관 키는 건너뛴다) */
/** 하위 업종 · 전용 대상(2026-10-05f · g) 키 — 「업종」 · 「전용 대상」 칸이 따로 보여 주므로 추가 조건 칩으로는 만들지 않는다 · tech(기술 기반 창업)는 체크 칸(order 에 있을 때만 칩) */
const TARGET_KEYS = ['subs', 'sec', 'sec_off', 'sec_auto', 'tech'];
export function ruleChips(r: RuleObj | null | undefined, order: readonly string[], skip: readonly string[] = ['meta']): RuleChip[] {
  if (!r) return [];
  const out: RuleChip[] = [];
  const seen = new Set<string>();
  for (const k of order) {
    seen.add(k);
    const v = r[k];
    if (v === undefined || v === null) continue;
    // 알 수 없는 자격(대표 신용점수 등)은 보이지 않는다 — 판정에도 안 쓰고 FP 에게도 안 묻는다 (2026-10-04 지도사). 예전 값은 저장만 유지
    if (k === 'chk') continue;
    // 빈 목록·빈 객체·false 는 «조건 없음» (서버도 버린다) — 칩을 만들지 않는다
    if ((Array.isArray(v) && !v.length) || (isObj(v) && !Object.keys(v).length) || v === false || v === '') continue;
    const t = ruleText(k, v);
    out.push(t ? { k, text: t } : { k, text: `형식 오류 · ${KIND_LABEL[k] || k}`, bad: true });
  }
  for (const k of Object.keys(r)) {
    if (seen.has(k) || skip.includes(k) || TARGET_KEYS.includes(k)) continue;
    const v = r[k];
    if (v === undefined || v === null) continue;
    out.push({ k, text: `알 수 없는 조건 · ${k}`, bad: true });
  }
  return out;
}

/** 확장 조건 한 줄 (AI 근거 상자) */
export function conditionsSummary(c: GovConditions | null | undefined): string {
  return ruleChips(c as RuleObj | undefined, COND_ORDER).map(x => x.text).join(' · ');
}

/** 세부 유형 한 줄 (AI 근거 상자) — 「고위험 작업개선 3,000만원 · 전동지게차 1,500만원」 */
export function tracksSummary(ts: GovTrack[] | null | undefined): string {
  if (!Array.isArray(ts)) return '';
  return ts.filter(t => isObj(t) && String(t.name || '').trim())
    .map(t => [String(t.name).trim(), String(t.amount || '').trim() || (isNum(t.amount_max_manwon) ? `${fmtNum(t.amount_max_manwon)}만원` : '')].filter(Boolean).join(' '))
    .join(' · ');
}
